import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Spinner,
} from '@chakra-ui/react'
import { TicketService } from '@/lib/services/ticket'
import type { AgentOption } from '@/types/ticket'

type Props = {
  assignedTo: number | ''
  assigneeName?: string | null
  onChange: (userId: number | '', displayName: string) => void
  label?: string
  isDisabled?: boolean
}

export default function TaskAssigneePicker({
  assignedTo,
  assigneeName,
  onChange,
  label = 'Assignee',
  isDisabled = false,
}: Props) {
  const [search, setSearch] = useState(assigneeName ?? '')
  const [results, setResults] = useState<AgentOption[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setSearch(assigneeName ?? '')
  }, [assigneeName])

  const searchAssignees = useCallback(async (term: string) => {
    setSearch(term)
    if (term.length < 2) {
      setResults([])
      return
    }
    setLoading(true)
    try {
      const res = await TicketService.searchHelpdeskAgentOptions(term)
      setResults(res.success && res.data ? res.data : [])
    } finally {
      setLoading(false)
    }
  }, [])

  const agentLabel = (agent: AgentOption) =>
    [agent.first_name, agent.last_name].filter(Boolean).join(' ') ||
    agent.user_login ||
    `#${agent.id}`

  return (
    <FormControl>
      <FormLabel fontSize="xs" mb={1}>
        {label}
      </FormLabel>
      <Input
        size="sm"
        value={search}
        onChange={(e) => void searchAssignees(e.target.value)}
        placeholder="Search by name (min 2 chars)"
        isDisabled={isDisabled}
      />
      {assignedTo !== '' && !search ? (
        <Box mt={1} fontSize="xs" color="gray.500">
          Selected user #{assignedTo}
        </Box>
      ) : null}
      {loading ? <Spinner size="sm" mt={2} /> : null}
      {results.length > 0 ? (
        <Box mt={2} borderWidth="1px" borderRadius="md" maxH="160px" overflowY="auto">
          {results.map((agent) => (
            <Button
              key={agent.id}
              variant="ghost"
              size="sm"
              w="100%"
              justifyContent="flex-start"
              onClick={() => {
                onChange(agent.id, agentLabel(agent))
                setSearch(agentLabel(agent))
                setResults([])
              }}
            >
              {agentLabel(agent)}
            </Button>
          ))}
        </Box>
      ) : null}
    </FormControl>
  )
}
